import { sortQuotesByBest } from '@src/utils/rfq';
import {
  FieldValidationLevel,
  NumericField,
  QuoteStatusEnum,
  SideEnum,
  Unit,
  getScaleFromIncrement,
  isFilledQuoteStatus,
  isTerminalQuoteStatus,
  isValidQuotePrice,
  toBig,
  toBigWithDefault,
  type Currency,
  type FieldValidationRule,
  type Quote,
  type QuoteMarket,
} from '@talos/kyoko';
import Big from 'big.js';

export const RFQSweeperFillExecutionStatus = {
  Staged: 'staged',
  Pending: 'pending',
  Filled: 'filled',
  Error: 'error',
} as const;
export type RFQSweeperFillExecutionStatusValue =
  (typeof RFQSweeperFillExecutionStatus)[keyof typeof RFQSweeperFillExecutionStatus];

export interface RFQSweeperFillExecutionState {
  status: RFQSweeperFillExecutionStatusValue;
  tooltip?: string;
}

/**
 * A selectable sweep quote.
 *
 * Quote sweeping is fed by quote rows where each row contributes its first market as the
 * executable market. Keep the parent quote and market bundled for better devx.
 */
export interface RFQSweeperQuote {
  quote: Quote;
  quoteMarket: QuoteMarket;
}

export interface RFQSweeperFill {
  /** Quantity currently available on this market for the sweeper side. */
  availableQty: Big;
  /** Executable price for this market on the sweeper side. */
  px: string;
  /** User-editable quantity for this market. Field errors are suitable for input tooltips. */
  desiredQty: NumericField;
  executionStatus: RFQSweeperFillExecutionState;
  quote: RFQSweeperQuote;
}

export interface RFQSweeperCurrentFillSummary {
  qty: string;
  avgPx: string | undefined;
}

interface RFQSweeperState {
  quotes: RFQSweeperQuote[];
  fills: RFQSweeperFill[];
  side: SideEnum;
  currency: Currency | undefined;
  orderQty: string | undefined;
  canPartiallyFillAtMarkets: boolean;
  marketDisplayNameByName: Map<string, string> | undefined;
}

interface RFQSweeperFillValidationContext {
  fill: RFQSweeperFill;
  marketDisplayNameByName: Map<string, string> | undefined;
}

interface RFQSweeperCreateFromQuotesParams {
  quotes: Quote[];
  side: SideEnum;
  currency: Currency | undefined;
  canPartiallyFillAtMarkets?: boolean;
  marketDisplayNameByName?: Map<string, string>;
}

interface RFQSweeperCreateParams {
  quotes: RFQSweeperQuote[];
  side: SideEnum;
  currency: Currency | undefined;
  canPartiallyFillAtMarkets?: boolean;
  marketDisplayNameByName?: Map<string, string>;
}

/**
 * Immutable helper for staging an RFQ sweep across multiple quote markets.
 *
 * Every mutating action returns a new RFQSweeper instance. `fills` contains the selected
 * markets to trade, and each fill owns a `NumericField` so UI inputs can use the same
 * validation state and `errorString` tooltip text as the rest of OMS.
 *
 * The sweeper is intentionally state-only. It exposes small transition methods for UI
 * updates and execution status changes, while `executeRFQSweeper` performs websocket/API
 * side effects and feeds updated sweeper instances back through an `onUpdate` callback.
 *
 * RFQ sweeping receives one `RFQSweeperQuote` per selectable market. Metadata shared by
 * the basket, like total `OrderQty`, comes from the first quote. Per-market execution
 * metadata, like RFQID and QuoteID, comes from the selected fill's bundled quote.
 */
export class RFQSweeper {
  public readonly quotes: RFQSweeperQuote[];
  public readonly fills: RFQSweeperFill[];
  public readonly side: SideEnum;
  public readonly currency: Currency | undefined;
  public readonly orderQty: string | undefined;
  public readonly canPartiallyFillAtMarkets: boolean;
  private readonly marketDisplayNameByName: Map<string, string> | undefined;

  private constructor({
    quotes,
    fills,
    side,
    currency,
    orderQty,
    canPartiallyFillAtMarkets,
    marketDisplayNameByName,
  }: RFQSweeperState) {
    this.quotes = [...quotes];
    this.fills = fills.map(fill => ({ ...fill }));
    this.side = side;
    this.currency = currency;
    this.orderQty = orderQty;
    this.canPartiallyFillAtMarkets = canPartiallyFillAtMarkets;
    this.marketDisplayNameByName = marketDisplayNameByName;
  }

  public static createFromQuotes({
    quotes,
    side,
    currency,
    canPartiallyFillAtMarkets,
    marketDisplayNameByName,
  }: RFQSweeperCreateFromQuotesParams): RFQSweeper {
    return RFQSweeper.create({
      quotes: getSweeperQuotesFromQuotes(quotes),
      side,
      currency,
      canPartiallyFillAtMarkets,
      marketDisplayNameByName,
    });
  }

  /** Creates a sweeper from already-paired quotes, preserving quote/market ownership. */
  public static create({
    quotes,
    side,
    currency,
    canPartiallyFillAtMarkets = true,
    marketDisplayNameByName,
  }: RFQSweeperCreateParams): RFQSweeper {
    const initialSide = getSideFromExecutedQuotes(quotes) ?? side;
    const sweeper = new RFQSweeper({
      quotes,
      fills: [],
      side: initialSide,
      currency,
      orderQty: quotes.at(0)?.quote.OrderQty,
      canPartiallyFillAtMarkets,
      marketDisplayNameByName,
    });

    return sweeper.withState({
      fills: sweeper.createExecutedFills(quotes),
    });
  }

  /** Recreates the sweeper with a new side so side-derived fill fields cannot leak across toggles. */
  public updateSide(side: SideEnum): RFQSweeper {
    if (side === this.side || this.hasExecutedFills) {
      // We don't let you change side if there is already some execution performed
      return this;
    }

    return RFQSweeper.create({
      quotes: this.quotes,
      side,
      currency: this.currency,
      canPartiallyFillAtMarkets: this.canPartiallyFillAtMarkets,
      marketDisplayNameByName: this.marketDisplayNameByName,
    });
  }

  /** Selects a market for sweeping and defaults desired quantity to the remaining RFQ quantity. */
  public addMarket(marketName: string): RFQSweeper {
    if (this.fills.some(fill => getMarketName(fill.quote) === marketName)) {
      return this;
    }

    const quote = findSweeperQuote(this.quotes, marketName);
    if (!quote) {
      return this;
    }

    const availableQty = this.getAvailableQty(quote.quoteMarket);
    const remainingQtyToStage = this.remainingUnallocatedQtyBig;

    // Different logic depending on if we are able to partially fill at the makers or not, also dependent on if we are breaching the total or not
    const desiredQty = this.canPartiallyFillAtMarkets
      ? availableQty.lt(remainingQtyToStage)
        ? availableQty
        : remainingQtyToStage
      : availableQty;
    const fill = this.createStagedFill(quote, desiredQty);

    return this.withState({
      fills: [...this.fills, fill],
    });
  }

  /** Removes a selected market by `QuoteMarket.Market`. */
  public removeMarket(marketName: string): RFQSweeper {
    const fills = this.fills.filter(fill => getMarketName(fill.quote) !== marketName || isExecutedFill(fill));
    return this.withState({
      fills,
    });
  }

  /** Updates the user-editable desired quantity for a selected market. */
  public setDesiredQty(marketName: string, qty: string): RFQSweeper {
    if (!this.canPartiallyFillAtMarkets) {
      return this;
    }

    return this.withState({
      fills: this.fills.map(fill => {
        if (getMarketName(fill.quote) !== marketName) {
          return fill;
        }

        return this.validateFill({
          ...fill,
          desiredQty: fill.desiredQty.updateValue(qty),
        });
      }),
    });
  }

  /**
   * Stages the best available open markets until the RFQ order quantity is covered.
   *
   * Quotes are selected by executable price. With partial fills available, the last
   * selected maker may be sized down to exactly complete the RFQ quantity. Without partial
   * fills, maker quantities are all-or-nothing, so priming stops before the next maker
   * would exceed the remaining RFQ quantity.
   *
   * Executed fills are basket history, so they remain selected. Every non-filled row is
   * cleared so repeated clicks recalculate against fresh quote qty/price.
   */
  public primeBestFills(): RFQSweeper {
    const orderQty = toBigWithDefault(this.orderQty, 0);
    if (orderQty.lte(0)) {
      return this;
    }

    // Executed fills already consumed part of the target order qty, so keep
    // them and only prime the portion that is still open.
    const retainedFills = this.fills.filter(isExecutedFill);
    const retainedTotalQty = retainedFills.reduce(
      (total, fill) => total.plus(toBigWithDefault(fill.desiredQty.value, 0)),
      Big(0)
    );
    const remainingOrderQty = orderQty.minus(retainedTotalQty);
    if (remainingOrderQty?.lte(0)) {
      return this.withState({
        fills: retainedFills,
      });
    }

    // Only consider currently tradable, non-executed markets, ordered from the
    // best executable price outward for the selected side.
    let remainingQtyToPrime = remainingOrderQty;
    const alreadyExecutedMarketNames = new Set(retainedFills.map(fill => getMarketName(fill.quote)));
    const sortedTradableQuotes = this.getBestPriceOrderedQuotes(
      this.quotes.flatMap(quote => {
        if (alreadyExecutedMarketNames.has(getMarketName(quote)) || !this.isQuoteMarketTradable(quote)) {
          return [];
        }

        return [quote];
      })
    );

    // Stage fills until the remaining order qty is covered or no more tradable
    // quote quantity is available. When maker partial fills are unavailable,
    // each market is all-or-nothing: keep walking best price order, but stop
    // before staging a maker whose full available qty would overfill the RFQ.
    const primedFills = [...retainedFills];
    for (const quote of sortedTradableQuotes) {
      const availableQty = this.getAvailableQty(quote.quoteMarket);
      if (!this.canPartiallyFillAtMarkets && availableQty.gt(remainingQtyToPrime)) {
        // We have qty left to find, but the next best market is greater than that. Stop here. Let the total qty be partial.
        break;
      }

      const desiredQty = availableQty.lt(remainingQtyToPrime) ? availableQty : remainingQtyToPrime;
      if (desiredQty.lte(0)) {
        break;
      }

      primedFills.push(this.createStagedFill(quote, desiredQty));

      remainingQtyToPrime = remainingQtyToPrime.minus(desiredQty);
      if (remainingQtyToPrime.lte(0)) {
        break;
      }
    }

    return this.withState({
      fills: primedFills,
    });
  }

  /**
   * Applies fresh quote revisions while preserving selected fills by market.
   *
   * Selected fills keep their desired quantity and execution status when their market is
   * still present. Availability, price, status validation, and parent quote metadata are
   * refreshed from the matching `RFQSweeperQuote`. Selected fills whose market disappears
   * are removed. Newly executed markets are added so the basket reflects fills that arrive
   * from quote updates while the panel is already open.
   */
  public updateQuotes(quotes: RFQSweeperQuote[]): RFQSweeper {
    const effectiveSweeper = this.hasExecutedFills
      ? this
      : this.withState({ side: getSideFromExecutedQuotes(quotes) ?? this.side });
    const refreshedFills = effectiveSweeper.fills.flatMap(fill => {
      const quote = findSweeperQuote(quotes, getMarketName(fill.quote));
      if (!quote) {
        return [];
      }

      const market = quote.quoteMarket;
      const updatedFill = {
        ...fill,
        availableQty: effectiveSweeper.getAvailableQty(market),
        px: effectiveSweeper.getFillPx(fill, market),
        quote,
      };

      const refreshedFill = effectiveSweeper.canPartiallyFillAtMarkets
        ? updatedFill
        : {
            // If we cannot partially fill, our staged fills _have to be_ reset to whatever the maker changed their size to if they change their size.
            // This is not true for when partial fills is true - user can edit the desired qty themselves.
            // Also note that this cannot happen for executed fills -- the quotes are static after being executed.
            ...updatedFill,
            desiredQty: effectiveSweeper.createDesiredQtyField(
              effectiveSweeper.getMarketDisplayName(market.Market),
              updatedFill.availableQty
            ),
          };

      return [effectiveSweeper.validateFill(refreshedFill)];
    });
    const refreshedFillMarketNames = new Set(refreshedFills.map(fill => getMarketName(fill.quote)));
    const newlyExecutedFills = effectiveSweeper.createExecutedFills(
      quotes.filter(quote => !refreshedFillMarketNames.has(getMarketName(quote)))
    );

    return effectiveSweeper.withState({
      quotes,
      fills: [...refreshedFills, ...newlyExecutedFills],
      orderQty: quotes.at(0)?.quote.OrderQty,
    });
  }

  /** Sum of all selected desired quantities, even when validation errors block execution. */
  public get totalQty(): string {
    return this.totalQtyBig.toFixed();
  }

  /**
   * Display aggregate for the current sweep basket.
   *
   * `qty` sums current desired quantities across all selected fills. `avgPx` is weighted by
   * those same quantities, so a larger fill contributes proportionally more to the average.
   */
  public get currentFillSummary(): RFQSweeperCurrentFillSummary | undefined {
    if (this.fills.length === 0) {
      return undefined;
    }

    const qty = this.totalQtyBig;
    const notional = this.fills.reduce(
      (total, fill) => total.plus(toBigWithDefault(fill.desiredQty.value, 0).times(toBigWithDefault(fill.px, 0))),
      Big(0)
    );

    return {
      qty: qty.toFixed(),
      avgPx: qty.gt(0) ? notional.div(qty).toFixed() : undefined,
    };
  }

  public get loading(): boolean {
    return this.fills.some(fill => fill.executionStatus.status === RFQSweeperFillExecutionStatus.Pending);
  }

  public get executableFills(): RFQSweeperFill[] {
    return this.fills.filter(isExecutableFill);
  }

  /** Number of still-open quotes that would be canceled if the current executable fills all succeed. */
  public get autoCancelRemainingQuoteCount(): number {
    return this.getAutoCancelableRemainingQuotes(this.executableFills.map(fill => getMarketName(fill.quote))).length;
  }

  /**
   * Non-terminal quotes left after a successful sweep execution.
   *
   * `executedMarketNames` are excluded because local quote state can still show them as
   * non-terminal before the quote subscription catches up with the execution report.
   */
  public getAutoCancelableRemainingQuotes(executedMarketNames: Iterable<string> = []): Quote[] {
    const executedMarkets = new Set(executedMarketNames);

    return this.quotes.flatMap(({ quote, quoteMarket }) => {
      if (executedMarkets.has(quoteMarket.Market) || isTerminalQuoteStatus(quote.QuoteStatus)) {
        return [];
      }

      return [quote];
    });
  }

  public get hasExecutedFills(): boolean {
    return this.fills.some(isExecutedFill);
  }

  /** Quantity already filled in this sweep basket. */
  public get executedQty(): string {
    return this.executedQtyBig.toFixed();
  }

  /** Original RFQ quantity minus executed quantity, clamped at zero for display and validation. */
  public get remainingQty(): string {
    return this.remainingQtyBig.toFixed();
  }

  /** Total currently open quantity that can still be traded for the sweeper side. */
  public get availableTradableQty(): string {
    return this.availableTradableQtyBig.toFixed();
  }

  /** Best tradable market that is not already selected in the sweep basket. */
  public getNextBestAvailableMarket(): string | undefined {
    const selectedMarketNames = new Set(this.fills.map(fill => getMarketName(fill.quote)));
    const sortedAvailableQuotes = this.getBestPriceOrderedQuotes(
      this.quotes.flatMap(quote => {
        if (selectedMarketNames.has(getMarketName(quote)) || !this.isQuoteMarketTradable(quote)) {
          return [];
        }

        return [quote];
      })
    );

    return sortedAvailableQuotes.at(0)?.quoteMarket.Market;
  }

  /** Highlights when all currently tradable markets cannot cover the unfilled RFQ quantity. */
  public get hasInsufficientAvailableQty(): boolean {
    return this.remainingQtyBig.gt(this.availableTradableQtyBig);
  }

  /** Markets with executed fills are locked into the basket and cannot be deselected. */
  public get lockedMarketNames(): Set<string> {
    return new Set(this.fills.filter(isExecutedFill).map(fill => getMarketName(fill.quote)));
  }

  public get sweepDisabled(): boolean {
    return this.loading || !!this.error || !this.hasExecutableFills;
  }

  /** First validation error across selected fills. */
  public get error(): string | undefined {
    const fillError = this.fills.map(getFillError).find(error => error != null);

    if (fillError) {
      return fillError;
    }

    if (this.orderQty && this.totalQtyBig.gt(toBigWithDefault(this.orderQty, 0))) {
      return `Cumulative desired quantity exceeds total quantity ${this.orderQty}`;
    }

    return undefined;
  }

  public markMarketsPending(marketNames: Iterable<string>): RFQSweeper {
    return this.withFillExecutionStatuses(
      new Map([...marketNames].map(marketName => [marketName, { status: RFQSweeperFillExecutionStatus.Pending }]))
    );
  }

  /** Applies terminal execution results reported by `executeRFQSweeper`. */
  public markMarketsWithExecutionStatus(statusesByMarketName: Map<string, RFQSweeperFillExecutionState>): RFQSweeper {
    return this.withFillExecutionStatuses(statusesByMarketName);
  }

  private get hasExecutableFills(): boolean {
    return this.executableFills.length > 0;
  }

  private get totalQtyBig(): Big {
    return this.fills.reduce((total, fill) => total.plus(toBigWithDefault(fill.desiredQty.value, 0)), Big(0));
  }

  private get executedQtyBig(): Big {
    return this.fills
      .filter(isExecutedFill)
      .reduce((total, fill) => total.plus(toBigWithDefault(fill.desiredQty.value, 0)), Big(0));
  }

  private get remainingQtyBig(): Big {
    const remainingQty = toBigWithDefault(this.orderQty, 0).minus(this.executedQtyBig);
    return remainingQty.gt(0) ? remainingQty : Big(0);
  }

  private get remainingUnallocatedQtyBig(): Big {
    const remainingQty = toBigWithDefault(this.orderQty, 0).minus(this.totalQtyBig);
    return remainingQty.gt(0) ? remainingQty : Big(0);
  }

  /**
   * Sum of currently tradable market qty for the sweeper side, capped at the RFQ order qty.
   *
   * A sweep can have more open market liquidity than the user requested, but the panel's
   * available quantity represents how much of this RFQ can still be swept.
   */
  private get availableTradableQtyBig(): Big {
    const executedMarketNames = new Set(this.fills.filter(isExecutedFill).map(fill => getMarketName(fill.quote)));

    const availableTradableQty = this.quotes.reduce((total, quote) => {
      // Executed markets stay visible as basket history, but their qty is no longer available for new fills.
      if (executedMarketNames.has(getMarketName(quote)) || !this.isQuoteMarketTradable(quote)) {
        return total;
      }

      return total.plus(this.getAvailableQty(quote.quoteMarket));
    }, Big(0));

    const orderQty = toBig(this.orderQty);
    if (!orderQty) {
      return availableTradableQty; // shouldnt happen, just for ts
    }

    return availableTradableQty.gt(orderQty) ? orderQty : availableTradableQty;
  }

  private withFillExecutionStatuses(statusesByMarketName: Map<string, RFQSweeperFillExecutionState>): RFQSweeper {
    return this.withState({
      fills: this.fills.map(fill => {
        if (isExecutedFill(fill)) {
          return fill;
        }

        return {
          ...fill,
          executionStatus: statusesByMarketName.get(getMarketName(fill.quote)) ?? fill.executionStatus,
        };
      }),
    });
  }

  private withState(state: Partial<RFQSweeperState>): RFQSweeper {
    return new RFQSweeper({
      quotes: this.quotes,
      fills: this.fills,
      side: this.side,
      currency: this.currency,
      orderQty: this.orderQty,
      canPartiallyFillAtMarkets: this.canPartiallyFillAtMarkets,
      marketDisplayNameByName: this.marketDisplayNameByName,
      ...state,
    });
  }

  private createDesiredQtyField(marketDisplayName: string, availableQty: Big): NumericField {
    return new NumericField({
      name: `${marketDisplayName} qty`,
      isRequired: true,
      unit: Unit.Decimal,
      scale: getCurrencyScale(this.currency),
    }).updateValue(availableQty.toFixed(), true);
  }

  private createStagedFill(quote: RFQSweeperQuote, desiredQty: Big): RFQSweeperFill {
    const market = quote.quoteMarket;
    const marketDisplayName = this.getMarketDisplayName(market.Market);
    const availableQty = this.getAvailableQty(market);

    return this.validateFill({
      availableQty,
      px: this.getPx(market),
      desiredQty: this.createDesiredQtyField(marketDisplayName, desiredQty),
      executionStatus: { status: RFQSweeperFillExecutionStatus.Staged },
      quote,
    });
  }

  /**
   * Rehydrates fills for quote markets that already executed before this sweeper instance was created.
   *
   * This keeps reopened sweep panels aligned with the true basket status: executed markets are
   * preselected, locked into the basket, and excluded from future accept requests.
   */
  private createExecutedFills(quotes: RFQSweeperQuote[]): RFQSweeperFill[] {
    return quotes.flatMap(quote => {
      const market = quote.quoteMarket;
      if (!isExecutedQuoteMarket(market)) {
        return [];
      }

      const marketDisplayName = this.getMarketDisplayName(market.Market);
      const availableQty = this.getAvailableQty(market);
      const executedQty = toBigWithDefault(quote.quote.TradedQty, 0);

      return [
        {
          availableQty,
          px: this.getPx(market),
          desiredQty: this.createDesiredQtyField(marketDisplayName, executedQty),
          executionStatus: { status: RFQSweeperFillExecutionStatus.Filled },
          quote,
        },
      ];
    });
  }

  /** Helps you get the qty for a quoteMarket. Looks at side, and handles backward compat around Qty */
  private getAvailableQty(quoteMarket: QuoteMarket): Big {
    if (this.side === SideEnum.Buy) {
      return toBigWithDefault(quoteMarket.OfferQty ?? this.orderQty, 0);
    }

    return toBigWithDefault(quoteMarket.BidQty ?? this.orderQty, 0);
  }

  private getPx(quoteMarket: QuoteMarket): string {
    return this.side === SideEnum.Buy ? quoteMarket.OfferPx : quoteMarket.BidPx;
  }

  /**
   * Ranks paired sweeper quotes by their executable side price.
   *
   * `sortQuotesByBest` works on quote markets only, so map back by market name to keep the
   * parent RFQ quote bundled with the market we will stage. Buy sweeps consume offers
   * (lowest price first); sell sweeps consume bids (highest price first).
   */
  private getBestPriceOrderedQuotes(quotes: RFQSweeperQuote[]): RFQSweeperQuote[] {
    const quotesByMarketName = new Map(quotes.map(quote => [getMarketName(quote), quote]));
    return sortQuotesByBest({
      quotes: quotes.map(quote => quote.quoteMarket),
      pricingMode: quotes.at(0)?.quote.PricingMode,
      bestBy: 'px',
      side: this.side === SideEnum.Buy ? 'offer' : 'bid',
    }).flatMap(quoteMarket => quotesByMarketName.get(quoteMarket.Market) ?? []);
  }

  /** A quote can be primed only when the executable side has open qty and a valid RFQ price. */
  private isQuoteMarketTradable(quote: RFQSweeperQuote): boolean {
    const quoteMarket = quote.quoteMarket;
    return (
      quoteMarket.QuoteStatus === QuoteStatusEnum.Open &&
      this.getAvailableQty(quoteMarket).gt(0) &&
      isValidQuotePrice(this.getPx(quoteMarket), quoteMarket.QuoteStatus, quote.quote.PricingMode)
    );
  }

  private getFillPx(fill: RFQSweeperFill, quoteMarket: QuoteMarket): string {
    // Keep the basket quote column on the side-specific quote px. For SpreadToFixing fills,
    // TradedPx is the absolute execution price while BidPx/OfferPx is the displayed spread.
    return this.getPx(quoteMarket);
  }

  private getMarketDisplayName(marketName: string): string {
    return this.marketDisplayNameByName?.get(marketName) ?? marketName;
  }

  private validateFill(fill: RFQSweeperFill): RFQSweeperFill {
    // Executed fills are historical basket state. Quote refreshes can make their market
    // terminal, but that should not block retries for the remaining executable fills.
    if (isExecutedFill(fill)) {
      return fill;
    }

    const desiredQty = fill.desiredQty.validate([fillQuantityValidation], {
      fill,
      marketDisplayNameByName: this.marketDisplayNameByName,
    });

    return {
      ...fill,
      desiredQty: desiredQty.hasError ? desiredQty.setTouched(true) : desiredQty,
    };
  }
}

/** Finds the paired quote for a market name. Market name is the stable identity in the sweeper UI. */
function findSweeperQuote(quotes: RFQSweeperQuote[], marketName: string): RFQSweeperQuote | undefined {
  return quotes.find(quote => getMarketName(quote) === marketName);
}

/** Converts raw Quote rows into the quote/first-market pair expected by the sweeper. */
function getSweeperQuotesFromQuotes(quotes: Quote[]): RFQSweeperQuote[] {
  return quotes.flatMap(quote => {
    const quoteMarket = quote.Markets.at(0);

    return quoteMarket ? [{ quote, quoteMarket }] : [];
  });
}

/** Centralizes market identity access so paired quote fields do not leak into every caller. */
function getMarketName(quote: RFQSweeperQuote): string {
  return quote.quoteMarket.Market;
}

/** Error fills are executable by design so users can retry only failed markets. */
function isExecutableFill(fill: RFQSweeperFill): boolean {
  return (
    // error means that our attempt at executing failed for some reason. The user can try again.
    fill.executionStatus.status === RFQSweeperFillExecutionStatus.Staged ||
    fill.executionStatus.status === RFQSweeperFillExecutionStatus.Error
  );
}

function isExecutedFill(fill: RFQSweeperFill): boolean {
  return fill.executionStatus.status === RFQSweeperFillExecutionStatus.Filled;
}

function isExecutedQuoteMarket(quoteMarket: QuoteMarket): boolean {
  return isFilledQuoteStatus(quoteMarket.QuoteStatus);
}

function getSideFromExecutedQuotes(quotes: RFQSweeperQuote[]): SideEnum | undefined {
  const executedSides = new Set(
    quotes.flatMap(({ quote, quoteMarket }) => {
      if (!isExecutedQuoteMarket(quoteMarket)) {
        return [];
      }

      const tradedSide = quoteMarket.TradedSide ?? quote.TradedSide;
      return tradedSide ? [tradedSide] : [];
    })
  );

  if (executedSides.size > 1) {
    throw new Error('Unable to create RFQ sweeper with executed fills on different traded sides');
  }

  return [...executedSides][0];
}

/** Returns the user-facing validation message for the first invalid fill field. */
function getFillError(fill: RFQSweeperFill): string | undefined {
  const error = fill.desiredQty.errors.at(0);

  if (!error) {
    return undefined;
  }

  if (typeof error.message === 'string') {
    return error.message;
  }

  return fill.desiredQty.errorString || 'Desired quantity is invalid';
}

// Validation is attached to each fill's NumericField so the blotter input can surface
// the same error string that blocks confirm.
const fillQuantityValidation: FieldValidationRule<NumericField, RFQSweeperFillValidationContext> = (field, context) => {
  if (!context) {
    return null;
  }

  const { fill } = context;
  const market = fill.quote.quoteMarket;
  const marketName = market.Market;
  const marketDisplayName = context.marketDisplayNameByName?.get(marketName) ?? marketName;

  if (market.QuoteStatus !== QuoteStatusEnum.Open) {
    return {
      message: `${marketDisplayName} market is ${market.QuoteStatus}`,
      level: FieldValidationLevel.Error,
    };
  }

  if (field.bigValue?.eq(0)) {
    return {
      message: `${marketDisplayName} quantity must be greater than 0`,
      level: FieldValidationLevel.Error,
    };
  }

  if (field.bigValue?.gt(fill.availableQty)) {
    return {
      message: `${marketDisplayName} quantity exceeds available quantity ${fill.availableQty.toFixed()}`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

/** Desired quantity precision follows the RFQ currency; unit RFQs use whole numbers. */
function getCurrencyScale(currency: Currency | undefined): number {
  if (!currency) {
    return 0;
  }

  return getScaleFromIncrement(currency.MinIncrement);
}
